import React, { useState, useEffect, useRef, useCallback } from 'react';

// Define the props interface
interface DoubleRangeSliderProps {
  currentMin?: number;
  currentMax?: number;
  min?: number;
  max?: number;
  showDropdown?: boolean;
  nbHits?: number;
  onRefine?: (values: { currentMin: number; currentMax: number }) => void;
  onSetPriceMethod?: (method: 'text' | 'number') => void;
  showDollar?: boolean;
}

// Define event types
type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
type KeyboardEvent = React.KeyboardEvent<HTMLInputElement>;

const DoubleRangeSlider: React.FC<DoubleRangeSliderProps> = props => {
  const {
    currentMin,
    currentMax,
    min = 0,
    max = 10,
    showDropdown: isShowDropdown,
    nbHits,
    onRefine,
    onSetPriceMethod,
    showDollar: isShowDollar,
  } = props;

  const [sliderWidth, setSliderWidth] = useState<number>(260);
  const [offsetSliderWidth, setOffsetSliderWidth] = useState<number>(0);
  const [inputMin, setInputMin] = useState<number | string>(currentMin || min);
  const [inputMax, setInputMax] = useState<number | string>(currentMax || max);
  const [typeDebounce, setTypeDebounce] = useState<number | null>(null);
  const [isFinishedDragging, setIsFinishedDragging] = useState<boolean>(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const minValueRef = useRef<HTMLDivElement>(null);
  const maxValueRef = useRef<HTMLDivElement>(null);
  const minValueDragRef = useRef<HTMLDivElement>(null);
  const maxValueDragRef = useRef<HTMLDivElement>(null);

  // Initialize component and handle changes to slider values and dimensions
  useEffect(() => {
    if (!minValueRef.current || !maxValueRef.current || !sliderRef.current) return;

    if (currentMin !== undefined && currentMin <= min) {
      minValueRef.current.style.width = `0%`;
    } else if (currentMin !== undefined) {
      minValueRef.current.style.width = `${((currentMin - min) * 100) / (max - min)}%`;
    }

    if (currentMax !== undefined && currentMax >= max) {
      maxValueRef.current.style.width = `100%`;
    } else if (currentMax !== undefined) {
      maxValueRef.current.style.width = `${((currentMax - min) * 100) / (max - min)}%`;
    }

    const sliderPos = sliderRef.current.getBoundingClientRect();

    setSliderWidth(sliderPos.width);
    setOffsetSliderWidth(sliderPos.left);
    setInputMin(currentMin || min);
    setInputMax(currentMax || max);
  }, [currentMin, min, currentMax, max, nbHits, isShowDropdown]);

  // Handle finished dragging
  useEffect(() => {
    if (isFinishedDragging && onRefine) {
      setIsFinishedDragging(false);
      let minValue = Number(inputMin);
      let maxValue = Number(inputMax);

      if (isNaN(minValue) || minValue < min) minValue = min;
      if (isNaN(maxValue) || maxValue > max) maxValue = max;
      if (minValue > maxValue) minValue = maxValue;

      setInputMin(minValue);
      setInputMax(maxValue);

      onRefine({
        currentMin: minValue,
        currentMax: maxValue,
      });
    }
  }, [isFinishedDragging, inputMin, inputMax, onRefine]);

  const onMouseMoveMin = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const clientX = 'clientX' in e ? e.clientX : e.touches[0].pageX;
      const minValueThumb = minValueRef.current;

      if (!minValueThumb) return;

      const draggedWidth = clientX - offsetSliderWidth;
      const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
      const currentMinValue = Math.round(min + ((max - min) / 100) * draggedWidthInPercent);
      onSetPriceMethod?.('number');

      if (currentMax !== undefined && currentMinValue >= currentMax) {
        return;
      }

      if (draggedWidthInPercent <= 0.1) {
        minValueThumb.style.width = `0%`;
        setInputMin(min);
      } else if (draggedWidthInPercent >= 99.9) {
        minValueThumb.style.width = `100%`;
        setInputMin(max);
      } else {
        minValueThumb.style.width = `${draggedWidthInPercent}%`;
        setInputMin(currentMinValue);
      }
    },
    [sliderWidth, offsetSliderWidth, min, max, currentMax, onSetPriceMethod]
  );

  const onMouseMoveMax = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const maxValueThumb = maxValueRef.current;
      const clientX = 'clientX' in e ? e.clientX : e.touches[0].pageX;

      if (!maxValueThumb) return;

      const draggedWidth = clientX - offsetSliderWidth;
      const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
      const currentMaxValue = Math.round(min + ((max - min) / 100) * draggedWidthInPercent);
      onSetPriceMethod?.('number');

      if (currentMin !== undefined && currentMaxValue <= currentMin) {
        return;
      }

      if (draggedWidthInPercent >= 99.9) {
        maxValueThumb.style.width = `100%`;
        setInputMax(max);
      } else if (draggedWidthInPercent <= 0.1) {
        maxValueThumb.style.width = `0%`;
        setInputMax(min);
      } else {
        maxValueThumb.style.width = `${draggedWidthInPercent}%`;
        setInputMax(currentMaxValue);
      }
    },
    [sliderWidth, offsetSliderWidth, min, max, currentMin, onSetPriceMethod]
  );

  const changeBetween = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        const clientX = e.clientX;
        const draggedWidth = clientX - offsetSliderWidth;
        const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;

        setIsFinishedDragging(true);
        if (draggedWidthInPercent <= 50) {
          onMouseMoveMin(e.nativeEvent);
        } else {
          onMouseMoveMax(e.nativeEvent);
        }
      }
    },
    [sliderWidth, offsetSliderWidth, onMouseMoveMin, onMouseMoveMax]
  );

  const changeMin = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        setIsFinishedDragging(true);
        onMouseMoveMin(e.nativeEvent);
      }
    },
    [onMouseMoveMin]
  );

  const changeMax = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        setIsFinishedDragging(true);
        onMouseMoveMax(e.nativeEvent);
      }
    },
    [onMouseMoveMax]
  );

  const onMouseUpMin = useCallback(() => {
    setIsFinishedDragging(true);
    document.removeEventListener('mouseup', onMouseUpMin);
    document.removeEventListener('mousemove', onMouseMoveMin);
    document.removeEventListener('touchend', onMouseUpMin);
    document.removeEventListener('touchmove', onMouseMoveMin);
  }, [onMouseMoveMin]);

  const changeMinValue = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      document.addEventListener('mousemove', onMouseMoveMin);
      document.addEventListener('mouseup', onMouseUpMin);
      document.addEventListener('touchmove', onMouseMoveMin);
      document.addEventListener('touchend', onMouseUpMin);
    },
    [onMouseMoveMin, onMouseUpMin]
  );

  const onMouseUpMax = useCallback(() => {
    setIsFinishedDragging(true);
    document.removeEventListener('mouseup', onMouseUpMax);
    document.removeEventListener('mousemove', onMouseMoveMax);
    document.removeEventListener('touchend', onMouseUpMax);
    document.removeEventListener('touchmove', onMouseMoveMax);
  }, [onMouseMoveMax]);

  const changeMaxValue = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      document.addEventListener('mousemove', onMouseMoveMax);
      document.addEventListener('mouseup', onMouseUpMax);
      document.addEventListener('touchmove', onMouseMoveMax);
      document.addEventListener('touchend', onMouseUpMax);
    },
    [onMouseMoveMax, onMouseUpMax]
  );

  const setMin = useCallback(
    (e: InputChangeEvent) => {
      onSetPriceMethod?.('text');

      const inputMinValue = e.target.value
        ? e.target.value.charAt(0) === '$'
          ? e.target.value.substring(1)
          : e.target.value
        : '';

      setInputMin(inputMinValue);

      const numericValue = Number(inputMinValue);
      if (
        !isNaN(numericValue) &&
        numericValue >= min &&
        currentMax !== undefined &&
        numericValue <= currentMax &&
        minValueRef.current
      ) {
        minValueRef.current.style.width = `${((numericValue - min) * 100) / (max - min)}%`;
      }
    },
    [min, max, currentMax, onSetPriceMethod]
  );

  const setMax = useCallback(
    (e: InputChangeEvent) => {
      onSetPriceMethod?.('text');

      const inputMaxValue = e.target.value
        ? e.target.value.charAt(0) === '$'
          ? e.target.value.substring(1)
          : e.target.value
        : '';

      setInputMax(inputMaxValue);

      const numericValue = Number(inputMaxValue);
      if (
        !isNaN(numericValue) &&
        currentMin !== undefined &&
        numericValue >= currentMin &&
        numericValue <= max &&
        maxValueRef.current
      ) {
        maxValueRef.current.style.width = `${((numericValue - min) * 100) / (max - min)}%`;
      }
    },
    [max, min, currentMin, onSetPriceMethod]
  );

  const preventEnter = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const mouseEnter = useCallback(() => {
    if (!sliderRef.current) return;

    const sliderPos = sliderRef.current.getBoundingClientRect();
    setSliderWidth(sliderPos.width);
    setOffsetSliderWidth(sliderPos.left);
  }, []);

  const debounceType = useCallback(
    (e: InputChangeEvent, handler: (e: InputChangeEvent) => void) => {
      // Immediately update the visual state
      handler(e);

      if (typeDebounce) {
        clearTimeout(typeDebounce);
      }
      if (e.target.value) {
        const timeoutId = window.setTimeout(() => {
          setIsFinishedDragging(true);
        }, 2000);
        setTypeDebounce(timeoutId);
      }
    },
    [typeDebounce]
  );

  return (
    <div ref={sliderRef} onMouseEnter={mouseEnter} onClick={changeMax} id="slider">
      <div ref={minValueRef} onClick={changeMin} id="min">
        <div ref={minValueDragRef} id="min-drag" onMouseDown={changeMinValue} onTouchStart={changeMinValue} />
      </div>

      <div ref={maxValueRef} onClick={changeBetween} id="max">
        <div ref={maxValueDragRef} id="max-drag" onMouseDown={changeMaxValue} onTouchStart={changeMaxValue} />
      </div>

      <div className="values">
        <label htmlFor="min-input" className="price-minimum-label">
          Minimum
        </label>
        <input
          id="min-input"
          type="text"
          onChange={e => debounceType(e, setMin)}
          onKeyDown={preventEnter}
          value={(isShowDollar ? '$' : '') + inputMin}
          autoComplete="off"
        />
        <label htmlFor="max-input" className="price-maximum-label">
          Maximum
        </label>
        <input
          id="max-input"
          type="text"
          onChange={e => debounceType(e, setMax)}
          onKeyDown={preventEnter}
          value={(isShowDollar ? '$' : '') + inputMax}
          autoComplete="off"
        />
      </div>
    </div>
  );
};

export default DoubleRangeSlider;
